import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

const TipsAndNotes = () => {
  const { t, language } = useLanguage();

  const tipsItems = [
    {
      id: 'punctuality',
      icon: 'fas fa-clock',
      title: t('tipsAndNotes.punctuality.title'),
      description: t('tipsAndNotes.punctuality.description')
    },
    // {
    //   id: 'adultsOnly',
    //   icon: 'fas fa-heart',
    //   title: t('tipsAndNotes.adultsOnly.title'),
    //   description: t('tipsAndNotes.adultsOnly.description')
    // },
    {
      id: 'jacket',
      icon: 'fas fa-tshirt',
      title: t('tipsAndNotes.jacket.title'),
      description: t('tipsAndNotes.jacket.description')
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  // Icon animation variants
  const iconVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.5,
      rotate: -180
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.8,
        delay: 0.3,
        type: "spring",
        stiffness: 200,
        damping: 15
      }
    },
    hover: {
      scale: 1.2,
      rotate: 10,
      transition: {
        duration: 0.3,
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    }
  };

  // Specific animations for different icons
  const getIconAnimation = (iconId) => {
    switch (iconId) {
      case 'punctuality':
        return {
          ...iconVariants,
          visible: {
            ...iconVariants.visible,
            transition: {
              ...iconVariants.visible.transition,
              delay: 0.3
            }
          },
          hover: {
            ...iconVariants.hover,
            rotate: 15
          }
        };
      case 'adultsOnly':
        return {
          ...iconVariants,
          visible: {
            ...iconVariants.visible,
            transition: {
              ...iconVariants.visible.transition,
              delay: 0.5
            }
          },
          hover: {
            ...iconVariants.hover,
            scale: 1.3,
            rotate: -10
          }
        };
      case 'jacket':
        return {
          ...iconVariants,
          visible: {
            ...iconVariants.visible,
            transition: {
              ...iconVariants.visible.transition,
              delay: 0.7
            }
          },
          hover: {
            ...iconVariants.hover,
            scale: 1.15,
            rotate: 5
          }
        };
      default:
        return iconVariants;
    }
  };

  return (
    <section id="tips" className="py-3 py-md-5 bg-white">
      <div className="container">
        <motion.div 
          className="text-center mb-5"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title">{t('tipsAndNotes.title')}<div className="title-decoration"><img src="/images/section-title.png" alt="Decorative element" /></div></h2>
          <p className="lead">{t('tipsAndNotes.subtitle')}</p>
        </motion.div>
        
        <div className="row justify-content-center">
          {tipsItems.map((item, index) => (
            <motion.div 
              key={index}
              className="col-lg-4 col-md-6 col-sm-10 mx-auto mb-4"
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
            >
              <div className="card border-0 shadow h-100">
                <div className="card-body text-center p-4">
                  <div className="icon-wrapper mb-3">
                    <motion.i 
                      className={`${item.icon} fa-2x`} 
                      style={{ color: 'var(--primary-color)' }}
                      variants={getIconAnimation(item.id)}
                      initial="hidden"
                      whileInView="visible"
                      whileHover="hover"
                      viewport={{ once: true }}
                    />
                  </div>
                  <h5 className="card-title mb-3" style={{ color: 'var(--text-dark)' }}>
                    {item.title}
                  </h5>
                  <p className="card-text text-muted">
                    {item.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="text-center mt-2 mt-md-4"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="alert alert-light border-primary" role="alert" style={{ borderColor: 'var(--primary-color)' }}>
            <i className="fas fa-heart me-2" style={{ color: 'var(--primary-color)' }}></i>
            <strong>{t('tipsAndNotes.finalNote.title')}:</strong> {t('tipsAndNotes.finalNote.description')}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TipsAndNotes;
